import { useCallback, useState } from 'react';
import { Badge, Card, Group, Tabs } from '@mantine/core';
import type { UseFormReturnType } from '@mantine/form';
import {
  IconBolt,
  IconBuildingFactory, IconCalculator, IconCloud, IconCylinder,
  IconDroplet,
  IconFlame,
  IconPackage,
  IconTree,
} from '@tabler/icons-react';

import type {
  EnvironmentalCalculations,
  EnvironmentalDataFormData,
} from '../../types/reports.js';
import { BrennstoffeSection } from './environmental/BrennstoffeSection.js';
import { DrehofenSection } from './environmental/DrehofenSection.js';
import { EinsatzstoffeSection } from './environmental/EinsatzstoffeSection.js';
import { EmissionsminderungSection } from './environmental/EmissionsminderungSection.js';
import { KilnLoadSection } from './environmental/KilnLoadSection.js';
import { ProduktionSection } from './environmental/ProduktionSection.js';
import { SteinbruecheSection } from './environmental/SteinbruecheSection.js';
import { StromSection } from './environmental/StromSection.js';
import { WasserSection } from './environmental/WasserSection.js';
import { useTabNavigation } from './TabNavContext.js';
import { ReportTabPanel, TabErrorDot, TabWarningDot } from './ReportTabs.js';
import { hasFieldErrors, tabsWithErrors } from './tabErrors.js';
import { useFieldTabMap, useTabFieldDiscovery } from '../form/fieldTabRegistry.js';
import { ENVIRONMENTAL_TAB_ORDER, ENVIRONMENTAL_TAB_TITLES } from './reportTabTitles.js';
import { useAllFieldWarnings } from '../form/fieldWarnings.js';

interface EnvironmentalDataFormProps {
  form: UseFormReturnType<EnvironmentalDataFormData>;
  runValidation: () => void;
  /** Live-Vorschau des Wasserverbrauchs, aus useReportCalc (D-10) -- der Litmus-Test der Phase. */
  calc: EnvironmentalCalculations | null;
  disabled?: boolean;
  loading?: boolean;
  /**
   * 27.1-07 (D-11): Berichtsjahr fuer die Drehofen-Abschnittsueberschrift "Jahresmittelwerte
   * {Jahr}". Gehoert zum Bericht (ReportEditPage), nicht zu den Formularwerten -- deshalb als
   * reiner Anzeige-Prop durchgereicht statt als Feld im Formularbaum.
   */
  reportingPeriod?: string;
  /**
   * Plan 43-09 schaltet es ein, wenn der Prueffer die Aenderungsliste sehen soll: nur dann
   * muessen alle Reiter einmal mounten, damit sich jede Beschriftung anmeldet (D-13).
   */
  discoverAllFields?: boolean;
}

const FIRST_TAB = ENVIRONMENTAL_TAB_ORDER[0];

interface KilnCountBadgeProps {
  form: UseFormReturnType<EnvironmentalDataFormData>;
}

/**
 * Isolierte Blattkomponente (Muster NotesBadge.tsx): abonniert das rotaryKilns-Array per
 * form.watch, damit der Zaehler-Badge am Drehoefen-Tab beim Anlegen/Loeschen von Oefen
 * aktualisiert, ohne dass dieser Tab-Container selbst Zustand haelt.
 */
function KilnCountBadge({ form }: KilnCountBadgeProps) {
  const [count, setCount] = useState<number>(() => (form.getValues().rotaryKilns ?? []).length);

  const handleChange = useCallback(({ value }: { value: unknown }) => {
    setCount(Array.isArray(value) ? value.length : 0);
  }, []);

  form.watch('rotaryKilns', handleChange);

  if (count === 0) return null;
  return (
    <Badge size="xs" color="vdzYellow.4" circle>
      {count}
    </Badge>
  );
}

/**
 * Praesentations-Shell (D-01): die uncontrolled form-Instanz und die Wasser-Calc-
 * Verdrahtung leben nicht mehr hier, sondern in ReportEditPage/useReportCalc. Diese
 * Komponente reicht form/calc nur noch an ihre Tabs/Sections durch -- alle sieben Tabs
 * laufen jetzt ueber genau eine Prop-Grenze (form/disabled/loading), die letzte
 * Phase-18-Uebergangskonstruktion (lokal gespiegelter rotaryKilns-State) ist entfernt.
 */
export function EnvironmentalDataForm({
  form,
  runValidation,
  calc,
  disabled,
  loading,
  reportingPeriod,
  discoverAllFields,
}: EnvironmentalDataFormProps) {
  const { activeTab, setActiveTab } = useTabNavigation(ENVIRONMENTAL_TAB_ORDER);
  const firstTabActive = activeTab === FIRST_TAB;

  // Identisch zum Unfalldaten-Formular und ohne eine einzige Feldangabe: die Karte
  // "Feld -> Reiter" entsteht daraus, dass die geteilten Feldkomponenten sich beim Mounten
  // an ihrem Reiter anmelden (Plan 20-16). Fuer die 280-Felder-Formulare waere eine
  // deklarierte Liste nicht pflegbar gewesen.
  const fieldTabMap = useFieldTabMap();
  // Dieselbe Rechnung fuer beide Meldungsstufen: `tabsWithErrors` kennt nur "Pfad-zu-Text-Karte
  // plus Reiterkarte" und nimmt den Hinweisstand unveraendert entgegen.
  const warnings = useAllFieldWarnings();
  const discovering = useTabFieldDiscovery(
    ENVIRONMENTAL_TAB_ORDER,
    // Auch Hinweise muessen die Entdeckungsrunde ausloesen koennen: ohne sie bliebe ein Reiter,
    // den der Melder nie geoeffnet hat, unangemeldet -- und genau dort ist der Punkt noetig, weil
    // der Hinweis sonst niemanden erreicht.
    (hasFieldErrors(form.errors) || hasFieldErrors(warnings) || discoverAllFields === true) &&
      !loading
  );
  const errorTabs = tabsWithErrors(form.errors, fieldTabMap);
  const warningTabs = tabsWithErrors(warnings, fieldTabMap);

  /** Rot schlaegt gelb: ein Reiter mit Fehlern zeigt den roten Punkt, auch wenn er Hinweise traegt. */
  const tabDot = (tab: string) => {
    if (errorTabs.has(tab)) return <TabErrorDot />;
    if (warningTabs.has(tab)) return <TabWarningDot />;
    return undefined;
  };

  return (
    <div onBlur={runValidation}>
      {/* keepMountedMode="display-none": der Standardmodus von Mantine 9 rendert nicht
          aktive Panels in einer versteckten React-Activity, in der die Anmelde-Effekte der
          Felder NICHT laufen -- die Entdeckungsrunde bliebe wirkungslos. */}
      <Tabs
        value={activeTab}
        onChange={setActiveTab}
        keepMounted={discovering}
        keepMountedMode="display-none"
      >
        <Tabs.List>
          <Tabs.Tab
            value="produktion"
            leftSection={<IconBuildingFactory size={16} />}
            rightSection={tabDot('produktion')}
          >
            {ENVIRONMENTAL_TAB_TITLES.produktion}
          </Tabs.Tab>
          <Tabs.Tab
            value="strom"
            leftSection={<IconBolt size={16} />}
            rightSection={tabDot('strom')}
          >
            {ENVIRONMENTAL_TAB_TITLES.strom}
          </Tabs.Tab>
          <Tabs.Tab
            value="brennstoffe"
            leftSection={<IconFlame size={16} />}
            rightSection={tabDot('brennstoffe')}
          >
            {ENVIRONMENTAL_TAB_TITLES.brennstoffe}
          </Tabs.Tab>
          <Tabs.Tab
            value="einsatzstoffe"
            leftSection={<IconPackage size={16} />}
            rightSection={tabDot('einsatzstoffe')}
          >
            {ENVIRONMENTAL_TAB_TITLES.einsatzstoffe}
          </Tabs.Tab>
          <Tabs.Tab
            value="drehoefen"
            leftSection={<IconCylinder size={16} />}
            rightSection={
              /* Zaehler UND Meldungspunkt: Mantine gibt genau eine rightSection her, beide
                 Angaben gelten aber unabhaengig voneinander. */
              <Group gap={6} wrap="nowrap">
                <KilnCountBadge form={form} />
                {tabDot('drehoefen') ?? null}
              </Group>
            }
          >
            {ENVIRONMENTAL_TAB_TITLES.drehoefen}
          </Tabs.Tab>
          <Tabs.Tab
            value="frachten"
            leftSection={<IconCalculator size={16} />}
            // D-14 haelt dieses Blatt vom Einreichen fern: eine leere oder halb gefuellte
            // Frachtenberechnung blockiert nichts. Ein tatsaechlich falscher Wert meldet sich
            // hier aber sehr wohl -- seit D-20 tragen die 39 Eingabefelder
            // Datenintegritaets-Regeln (Assert\PositiveOrZero, MaxDecimalPlaces), und seit dem
            // CR-01-Fix kaskadiert der Validator auch in sie hinein. Die Felder melden sich wie
            // ueberall selbst an ihrem Reiter an (useFieldTabMap), der Punkt erscheint also ohne
            // weiteres Zutun.
            rightSection={tabDot('frachten')}
          >
            {ENVIRONMENTAL_TAB_TITLES.frachten}
          </Tabs.Tab>
          <Tabs.Tab
            value="emissionsminderung"
            leftSection={<IconCloud size={16} />}
            rightSection={tabDot('emissionsminderung')}
          >
            {ENVIRONMENTAL_TAB_TITLES.emissionsminderung}
          </Tabs.Tab>
          <Tabs.Tab
            value="steinbrueche"
            leftSection={<IconTree size={16} />}
            rightSection={tabDot('steinbrueche')}
          >
            {ENVIRONMENTAL_TAB_TITLES.steinbrueche}
          </Tabs.Tab>
          <Tabs.Tab
            value="wasser"
            leftSection={<IconDroplet size={16} />}
            rightSection={tabDot('wasser')}
          >
            {ENVIRONMENTAL_TAB_TITLES.wasser}
          </Tabs.Tab>
        </Tabs.List>

        <Card
          padding="lg"
          radius="md"
          withBorder
          style={firstTabActive ? { borderTopLeftRadius: 0 } : undefined}
        >
          <ReportTabPanel value="produktion">
            <ProduktionSection form={form} productionCalc={calc} disabled={disabled} loading={loading} />
          </ReportTabPanel>

          <ReportTabPanel value="strom">
            <StromSection form={form} disabled={disabled} loading={loading} />
          </ReportTabPanel>

          <ReportTabPanel value="brennstoffe">
            <BrennstoffeSection form={form} fuelCalc={calc} disabled={disabled} loading={loading} />
          </ReportTabPanel>

          <ReportTabPanel value="einsatzstoffe">
            <EinsatzstoffeSection form={form} materialCalc={calc} disabled={disabled} loading={loading} />
          </ReportTabPanel>

          <ReportTabPanel value="drehoefen">
            <DrehofenSection
              form={form}
              disabled={disabled}
              loading={loading}
              reportingPeriod={reportingPeriod}
            />
          </ReportTabPanel>

          <ReportTabPanel value="frachten">
            <KilnLoadSection
              form={form}
              loadCalc={calc}
              disabled={disabled}
              loading={loading}
              reportingPeriod={reportingPeriod}
            />
          </ReportTabPanel>

          <ReportTabPanel value="emissionsminderung">
            <EmissionsminderungSection form={form} disabled={disabled} loading={loading} />
          </ReportTabPanel>

          <ReportTabPanel value="steinbrueche">
            <SteinbruecheSection form={form} quarryCalc={calc} disabled={disabled} loading={loading} />
          </ReportTabPanel>

          <ReportTabPanel value="wasser">
            <WasserSection form={form} waterCalc={calc} disabled={disabled} loading={loading} />
          </ReportTabPanel>
        </Card>
      </Tabs>
    </div>
  );
}

export type { EnvironmentalDataFormProps };
